// ./src/components/features/CommandPalette/CommandPalette.tsx
//
// The global ⌘K / Ctrl+K command palette (Mantine Spotlight). Mounted once from
// DashboardLayout so it's available on every authenticated page.
//
// Two sources:
//   • Pages     — every navigable, permitted route from the sitemap (icons,
//                 translated names/subtitles, permission-filtered).
//   • Actions   — contextual commands the current page registers via
//                 usePageCommands (see useCommandPalette).
//
// Phase 1 covered navigation + page commands. Phase 2 (this file) adds async
// entity search — when the user stops typing (300 ms, ≥2 chars) the palette
// searches Contacts, Event guests, and Events in parallel and shows the matches
// as their own groups. The broader plan lives in ./PHASE2.md.

import React, { useEffect, useMemo, useState } from "react";
import {
  Spotlight,
  SpotlightActionData,
  SpotlightActionGroupData,
  SpotlightFilterFunction,
} from "@mantine/spotlight";
import { Loader } from "@mantine/core";
import { useOverlayStack } from "@em/ui";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import type { SiteMapItem } from "@/config/siteMap";
import { ACCESS } from "@/config/userRoles";
import { useCrewAccess } from "@/contexts/CrewAccessContext/CrewAccessContext";
import { useRedeemerOnly } from "@/hooks/useRedeemerOnly";
import { canUserAccess } from "@/utils/userRoleHelpers";
import { useApp } from "@/contexts";
import { useCurrentEvent } from "@/contexts/CurrentEventContext/CurrentEventContext";
import { usePaletteCommands, useEntitySearch } from "@/hooks";
import {
  EVENT_GUEST_ROLE,
  RsvpGroup,
  RsvpGroupService,
  getPersonDisplayName,
  getGuestDisplayName,
  getGuestRoleLabel,
  getRsvpStatusLabel,
} from "@/utils/api";
import { AppIcon } from "@/components/generic";
import { useBranding } from "@/contexts/BrandingContext";

type PaletteItem = SpotlightActionData | SpotlightActionGroupData;

// Async entity results are server-matched (by name/email/phone/code), so they must
// bypass the palette's client-side text filter — a match on phone won't appear in
// the label. Everything else (pages, actions) filters normally. We tag entity
// results with a dedicated `hit:` id prefix that can't collide with page/nav ids
// (the event-nav actions use `event:<url>` ids, which must still be filtered).
const ENTITY_PREFIX = "hit:";
const isEntityAction = (a: SpotlightActionData): boolean => String(a.id).startsWith(ENTITY_PREFIX);

const toKeywords = (kw: SpotlightActionData["keywords"]): string[] =>
  Array.isArray(kw) ? kw : kw ? [kw] : [];

const actionMatches = (a: SpotlightActionData, q: string): boolean => {
  if (!q) return true;
  const hay = [a.label, a.description, ...toKeywords(a.keywords)].filter(Boolean).join(" ").toLowerCase();
  return hay.includes(q);
};

const paletteFilter: SpotlightFilterFunction = (query, actions) => {
  const q = query.trim().toLowerCase();
  const keep = (a: SpotlightActionData) => isEntityAction(a) || actionMatches(a, q);
  const out: PaletteItem[] = [];
  for (const item of actions as PaletteItem[]) {
    if ("actions" in item) {
      const filtered = item.actions.filter(keep);
      if (filtered.length > 0) out.push({ ...item, actions: filtered });
    } else if (keep(item)) {
      out.push(item);
    }
  }
  return out;
};

// A route is offered when it's visible, permitted, points at a concrete URL
// (no ":id"/":eventId" params, not the catch-all) and isn't an event-workspace
// page (those need an active event id).
const isNavigable = (item: SiteMapItem, userRoles: string[]): boolean =>
  item.visible !== false &&
  item.scope !== "event" &&
  !!item.url &&
  !item.url.includes(":") &&
  item.url !== "*" &&
  canUserAccess(item.roles, userRoles);

export const CommandPalette: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["common"]);
  // A2e #101 — `{{brand}}` in a page subtitle is the brand from the white label.
  const brandName = useBranding().brand.name;
  const { siteMap, userRoles } = useApp();
  const { currentEventId, currentEvent } = useCurrentEvent();
  // The event's pages are judged by the viewer's role ON the event — their crew role in crew mode, as the sidebar does
  // (2026-10-18: the palette listed every event page to crew by their account role).
  const { isCrewMode, role: crewRole } = useCrewAccess();
  const eventRoles = useMemo(() => (isCrewMode ? (crewRole ? [crewRole] : []) : userRoles), [isCrewMode, crewRole, userRoles]);
  // A stall's voucher redeemer has no palette at all — one page, nothing to search (owner, 2026-10-18).
  const { redeemerOnly } = useRedeemerOnly();
  const pageCommands = usePaletteCommands();

  // Controlled query so we can debounce it into an async entity search.
  const [query, setQuery] = useState("");
  // On the overlay stack while open (2026-10-03): Esc / ⌘S typed in the palette must not reach a drawer under it.
  const [paletteOpen, setPaletteOpen] = useState(false);
  useOverlayStack(paletteOpen);
  const canSearchContacts = !redeemerOnly && canUserAccess(ACCESS.AUDIENCE, userRoles);
  const canSearchEvents = !redeemerOnly && canUserAccess(ACCESS.EVENT_MANAGE, userRoles);
  const { contacts, guests, events, loading } = useEntitySearch(query, {
    contacts: canSearchContacts,
    events: canSearchEvents,
  });

  // Resolve RSVP-group names for the guest hits (the guest record only carries the
  // id). Bounded — at most a few events across ≤6 guests — and non-blocking.
  const [groupNames, setGroupNames] = useState<Record<string, string>>({});
  useEffect(() => {
    const grouped = guests.filter((g) => g.rsvpGroupId && g.eventId);
    if (grouped.length === 0) {
      setGroupNames({});
      return;
    }
    const eventIds = Array.from(new Set(grouped.map((g) => g.eventId as string)));
    let alive = true;
    Promise.all(eventIds.map((eid) => RsvpGroupService.list(eid))).then((results) => {
      if (!alive) return;
      const map: Record<string, string> = {};
      for (const r of results) {
        if (r.code === OP_RESULT_CODES.OK && Array.isArray(r.data)) {
          (r.data as RsvpGroup[]).forEach((gr) => {
            if (gr.id && gr.name) map[gr.id] = gr.name;
          });
        }
      }
      setGroupNames(map);
    });
    return () => {
      alive = false;
    };
  }, [guests]);

  // ── The current event's pages (event-scope sitemap items, :eventId filled) ──
  const eventNavActions = useMemo(() => {
    if (!currentEventId) return [];

    const byUrl = new Map<string, SpotlightActionGroupData["actions"][number]>();

    const walk = (items: SiteMapItem[]) => {
      for (const item of items) {
        const accessible = canUserAccess(item.roles, eventRoles);

        if (item.scope === "event" && item.visible !== false && item.url && accessible) {
          const url = item.url.replace(":eventId", currentEventId);
          if (!url.includes(":") && url !== "*") {
            const tk = item.options?.translationKey;
            const label = (tk ? t(`common:pages.${tk}.name`, { defaultValue: "" }) : "") || item.name;
            const description =
              (tk ? t(`common:pages.${tk}.subtitle`, { defaultValue: "", brand: brandName }) : "") || item.subtitle || "";

            byUrl.set(url, {
              id: `event:${url}`,
              label,
              description,
              keywords: [item.name, label, currentEvent?.title ?? ""],
              leftSection: item.icon ? <AppIcon iconName={item.icon} size={18} /> : undefined,
              onClick: () => navigate(url),
            });
          }
        }

        if (item.items?.length && accessible) {
          walk(item.items);
        }
      }
    };

    walk(siteMap);

    // Keep the sitemap's natural order (event pages read best in workflow order).
    return Array.from(byUrl.values());
  }, [siteMap, eventRoles, currentEventId, currentEvent, t, navigate, brandName]);

  // ── Navigation actions from the sitemap ──
  const navActions = useMemo(() => {
    // Keyed by URL so a section and its first child (which share a URL) collapse
    // to one entry — the deeper/more specific one wins.
    const byUrl = new Map<string, SpotlightActionGroupData["actions"][number]>();

    const walk = (items: SiteMapItem[]) => {
      for (const item of items) {
        const accessible = canUserAccess(item.roles, userRoles);

        if (isNavigable(item, userRoles) && item.url) {
          const url = item.url;
          const tk = item.options?.translationKey;
          const label =
            (tk ? t(`common:pages.${tk}.name`, { defaultValue: "" }) : "") || item.name;
          const description =
            (tk ? t(`common:pages.${tk}.subtitle`, { defaultValue: "", brand: brandName }) : "") ||
            item.subtitle ||
            "";

          byUrl.set(url, {
            id: url,
            label,
            description,
            keywords: [item.name, label],
            leftSection: item.icon ? (
              <AppIcon iconName={item.icon} size={18} />
            ) : undefined,
            onClick: () => navigate(url),
          });
        }

        if (item.items?.length && accessible) {
          walk(item.items);
        }
      }
    };

    walk(siteMap);

    return Array.from(byUrl.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label))
    );
  }, [siteMap, userRoles, t, navigate, brandName]);

  // ── Contextual page commands ──
  const commandActions = useMemo(
    () =>
      pageCommands.map((command) => ({
        id: command.id,
        label: command.label,
        description: command.description,
        keywords: command.keywords,
        leftSection: command.icon ? (
          <AppIcon iconName={command.icon} size={18} />
        ) : undefined,
        onClick: command.onClick,
      })),
    [pageCommands]
  );

  // ── Async entity results (events / event guests / contacts) ──
  const entityGroups = useMemo(() => {
    const groups: SpotlightActionGroupData[] = [];
    const kw = [query];

    // One-line summary for a guest hit: role (crew only) · RSVP status · RSVP group
    // name (or party size when not grouped).
    const guestSummary = (g: (typeof guests)[number]): string => {
      const parts: string[] = [];
      if (g.guestRole && g.guestRole !== EVENT_GUEST_ROLE.GUEST) parts.push(getGuestRoleLabel(g.guestRole));
      if (g.rsvpStatus) parts.push(getRsvpStatusLabel(g.rsvpStatus));
      const groupName = g.rsvpGroupId ? groupNames[g.rsvpGroupId] : undefined;
      if (groupName) parts.push(groupName);
      else if ((g.partySize ?? 1) > 1) parts.push(t("common:commandPalette.partyOf", { defaultValue: "Party of {{n}}", n: g.partySize }));
      return parts.filter(Boolean).join(" · ");
    };

    if (events.length > 0) {
      groups.push({
        group: t("common:commandPalette.eventsGroup", { defaultValue: "Events" }),
        actions: events.map((e) => ({
          id: `hit:event:${e.id}`,
          label: e.title ?? "—",
          description: e.subtitle ?? "",
          keywords: kw,
          leftSection: <AppIcon iconName="CalendarEvent" size={18} />,
          onClick: () => navigate(`/events/${e.id}`),
        })),
      });
    }

    if (guests.length > 0) {
      groups.push({
        group: t("common:commandPalette.guestsGroup", { defaultValue: "Event guests" }),
        actions: guests.map((g) => ({
          id: `hit:guest:${g.id}`,
          label: getGuestDisplayName(g),
          description: guestSummary(g),
          keywords: kw,
          leftSection: <AppIcon iconName="Users" size={18} />,
          // Open the editor on the page the person actually belongs to: crew roles
          // go to Crew (even if they're also an attendee), plain guests to Guests.
          onClick: () => {
            if (!g.eventId || !g.id) return;
            const page = g.guestRole && g.guestRole !== EVENT_GUEST_ROLE.GUEST ? "crew" : "guests";
            navigate(`/events/${g.eventId}/${page}/${g.id}/edit`);
          },
        })),
      });
    }

    if (contacts.length > 0) {
      groups.push({
        group: t("common:commandPalette.contactsGroup", { defaultValue: "Contacts" }),
        actions: contacts.map((p) => ({
          id: `hit:contact:${p.id}`,
          label: getPersonDisplayName(p),
          description: [p.email, p.phone].filter(Boolean).join(" · "),
          keywords: kw,
          leftSection: <AppIcon iconName="AddressBook" size={18} />,
          // Open the contact's editor directly (route-based edit mode).
          onClick: () => navigate(`/audience/contacts/${p.id}/edit`),
        })),
      });
    }

    // A placeholder while the first search is in flight, so the list doesn't flash
    // "Nothing found" before results arrive.
    if (loading && contacts.length + guests.length + events.length === 0) {
      groups.push({
        group: t("common:commandPalette.searchGroup", { defaultValue: "Search" }),
        actions: [
          {
            id: "hit:loading",
            label: t("common:commandPalette.searching", { defaultValue: "Searching…" }),
            keywords: [query],
            leftSection: <Loader size="xs" />,
            onClick: () => {},
          },
        ],
      });
    }

    return groups;
  }, [contacts, guests, events, loading, query, groupNames, navigate, t]);

  const actions = useMemo(() => {
    const groups: SpotlightActionGroupData[] = [];

    // Found records (from the async search) lead — if you searched for something and
    // it matched, that's what you want first.
    groups.push(...entityGroups);

    if (commandActions.length > 0) {
      groups.push({
        group: t("common:commandPalette.actionsGroup", { defaultValue: "Actions" }),
        actions: commandActions,
      });
    }

    // The current event's pages come before the account-level pages.
    if (eventNavActions.length > 0) {
      groups.push({
        group: currentEvent?.title
          ? t("common:commandPalette.eventGroupNamed", { defaultValue: "{{name}}", name: currentEvent.title })
          : t("common:commandPalette.eventGroup", { defaultValue: "This event" }),
        actions: eventNavActions,
      });
    }

    groups.push({
      group: t("common:commandPalette.pagesGroup", { defaultValue: "Pages" }),
      actions: navActions,
    });

    return groups;
  }, [commandActions, eventNavActions, entityGroups, navActions, currentEvent, t]);

  if (redeemerOnly) {
    return null;
  }

  return (
    <Spotlight
      actions={actions}
      query={query}
      onQueryChange={setQuery}
      filter={paletteFilter}
      shortcut="mod + K"
      onSpotlightOpen={() => setPaletteOpen(true)}
      onSpotlightClose={() => setPaletteOpen(false)}
      scrollable
      maxHeight={460}
      highlightQuery
      nothingFound={t("common:commandPalette.nothingFound", {
        defaultValue: "Nothing found",
      })}
      searchProps={{
        leftSection: <AppIcon iconName="Search" size={18} />,
        placeholder: t("common:commandPalette.placeholder", {
          defaultValue: "Search pages, actions, contacts, guests, events…",
        }),
      }}
    />
  );
};
